"use client";

import React, { useEffect, useState } from "react";
import { P } from "./p";
import { Icon, IconName } from "./icon";

export interface Step {
  stepType: string;
  title: string;
  progress?: number; // 0-100
  iconName?: IconName;
}

export interface AccordionStepperProps {
  title: string;
  subtitle: string;
  steps: Step[];
  selectedStep?: string; // id of the selected step
  isDisabled?: boolean;
  onStepClick?: (stepId: string) => void;
}

export const AccordionStepper = ({
  title,
  subtitle,
  steps,
  selectedStep,
  isDisabled = false,
  onStepClick,
}: AccordionStepperProps) => {
  const [isExpanded, setIsExpanded] = useState(true);

  useEffect(() => {
    if (isDisabled) {
      setIsExpanded(false);
    }
  }, [isDisabled]);

  const toggleExpanded = () => {
    if (!isDisabled) {
      setIsExpanded(!isExpanded);
    }
  };

  const handleStepClick = (stepId: string) => {
    if (!isDisabled && onStepClick) {
      onStepClick(stepId);
    }
  };

  return (
    <div
      className={`bg-system-grayscale-00 w-full min-w-[330px] rounded-lg border border-black/5 shadow-md ${isDisabled ? "opacity-75" : ""}`}
    >
      {/* Header */}
      <div
        className="flex cursor-pointer items-center gap-2.5 p-3"
        onClick={toggleExpanded}
      >
        <div className="bg-brand-greens-light flex h-9 w-9 items-center justify-center rounded-full">
          <span className="text-brand-primary-darker text-base">
            {/* Icon would go here */}
          </span>
        </div>

        <div className="flex-1">
          <P className="text-brand-primary-regular/50 text-xs font-medium">
            {title}
          </P>
          <div className="flex w-full items-center justify-between">
            <P className="text-brand-primary-darker text-base font-medium">
              {subtitle}
            </P>
            <span className="text-brand-primary-regular/50 text-sm">
              {isExpanded ? "▲" : "▼"}
            </span>
          </div>
        </div>
      </div>

      {/* Divider */}
      {isExpanded && (
        <>
          <hr className="border-brand-primary-darker/20 mx-2 border-dashed" />

          {/* Steps */}
          <div className="flex flex-col">
            {steps.map((step) => (
              <div
                key={step.stepType}
                className={`flex items-center gap-2 p-3 transition-colors
                  ${selectedStep === step.stepType ? "bg-brand-beige-lighter" : "hover:bg-brand-beige-lighter"}
                  ${isDisabled ? "cursor-not-allowed" : "cursor-pointer"}`}
                onClick={() => !isDisabled && handleStepClick(step.stepType)}
              >
                <div className="relative">
                  {step.progress !== undefined &&
                  step.progress > 0 &&
                  step.progress < 100 ? (
                    <div className="relative h-9 w-9">
                      <div className="bg-system-grayscale-00 absolute inset-0 z-10 flex items-center justify-center rounded-full">
                        <span className="text-brand-primary-darker text-xs font-semibold">
                          {step.progress}%
                        </span>
                      </div>
                      {/* Progress circle would be rendered here with SVG */}
                    </div>
                  ) : (
                    <div
                      className={`flex h-9 w-9 items-center justify-center rounded-full ${
                        step.progress === 100
                          ? "bg-brand-greens-light/50"
                          : selectedStep === step.stepType
                            ? "bg-brand-greens-light"
                            : "bg-brand-beige-light"
                      }`}
                    >
                      {step.progress === 100 ? (
                        <Icon
                          name="check"
                          className="text-brand-primary-darker h-[22px] w-3.5"
                        />
                      ) : step.iconName ? (
                        <Icon
                          name={step.iconName}
                          className="text-brand-primary-darker h-[22px] w-4"
                        />
                      ) : null}
                    </div>
                  )}
                </div>

                <P
                  noSpacing
                  className="text-brand-primary-darker text-sm font-medium"
                >
                  {step.title}
                </P>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default AccordionStepper;
